/**
 * CollabCalendarList – liste-visning per konto med fargekodede type-blokker.
 * Klikk på et rektangel åpner samme DayOverview-modal som Grid.
 */
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";

const TYPE_COLOR = {
  sfs: "#E63946",         // rød
  mass: "#4DA8DA",        // blå
  l4l: "#F4B400",         // gul
  story_swap: "#46B85B",  // grønn
};
const TYPE_ORDER = ["sfs", "mass", "l4l", "story_swap"];
const TYPE_LABEL_KEY = {
  sfs: "collab.type_sfs",
  mass: "collab.type_mass",
  l4l: "collab.type_l4l",
  story_swap: "collab.type_story_swap",
};
const TYPE_FALLBACK = { sfs: "SFS", mass: "MASS", l4l: "L4L", story_swap: "STORY SWAP" };

export default function CollabCalendarList({
  year,
  month,
  monthLabel,
  prevMonth,
  nextMonth,
  slots,
  requests,
  externalMessages,
  manualAgreements,
  accounts,
  selectedAccountId,
  onSelectAccount,
  onDateClick,
  currentUserId,
}) {
  const { t } = useTranslation();

  // Filter slots by selected account (empty account_ids = gjelder alle)
  const filteredSlots = useMemo(() => {
    if (!selectedAccountId) return slots;
    return slots.filter((s) => {
      const ids = s.account_ids || [];
      return ids.length === 0 || ids.includes(selectedAccountId);
    });
  }, [slots, selectedAccountId]);

  const filteredRequests = useMemo(() => {
    if (!selectedAccountId) return requests;
    return requests.filter((r) => {
      const ids = r.account_ids || [];
      return ids.length === 0 || ids.includes(selectedAccountId);
    });
  }, [requests, selectedAccountId]);

  const slotByDate = useMemo(
    () => Object.fromEntries(filteredSlots.map((s) => [s.date, s])),
    [filteredSlots]
  );
  const requestsByDate = useMemo(() => {
    return filteredRequests.reduce((acc, r) => {
      const d = r.scheduled_date;
      if (!d) return acc;
      if (!acc[d]) acc[d] = [];
      acc[d].push(r);
      return acc;
    }, {});
  }, [filteredRequests]);
  const extByDate = useMemo(() => {
    return externalMessages.reduce((acc, m) => {
      if (!m.slot_date) return acc;
      if (!acc[m.slot_date]) acc[m.slot_date] = [];
      acc[m.slot_date].push(m);
      return acc;
    }, {});
  }, [externalMessages]);
  const manualByDate = useMemo(() => {
    return manualAgreements.reduce((acc, m) => {
      if (!acc[m.date]) acc[m.date] = [];
      acc[m.date].push(m);
      return acc;
    }, {});
  }, [manualAgreements]);

  // Bygg datoer i valgt måned
  const days = useMemo(() => {
    const pad = (n) => String(n).padStart(2, "0");
    const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const arr = [];
    for (let d = 1; d <= lastDay; d++) {
      arr.push(`${year}-${pad(month + 1)}-${pad(d)}`);
    }
    return arr;
  }, [year, month]);

  // Bestem status og label for én rektangel
  const cellFor = (date, type) => {
    // 1) Manuell avtale
    const manual = (manualByDate[date] || []).find((m) => m.type === type);
    if (manual) {
      const label = manual.partner_handle
        ? `@${manual.partner_handle}`
        : t("collab_list.manual", "Manuell");
      return { status: "booked", label };
    }
    // 2) Ekstern henvendelse
    const ext = (extByDate[date] || []).find((e) => e.slot_type === type);
    if (ext) {
      const label = ext.external_handle
        ? `@${ext.external_handle}`
        : t("collab_list.external", "Ekstern");
      return { status: "booked", label };
    }
    // 3) Intern collab_request
    const req = (requestsByDate[date] || []).find((r) => r.type === type);
    if (req) {
      const partner =
        req.requester_id === currentUserId ? req.target_username : req.requester_username;
      return {
        status: "booked",
        label: partner ? `@${partner}` : t("collab_list.booked", "Booket"),
      };
    }
    // 4) Slot åpen for typen
    const slot = slotByDate[date];
    if (slot && slot.is_available && (slot.types || []).includes(type)) {
      return { status: "free", label: t("collab_list.free", "LEDIG") };
    }
    // 5) Slot finnes men opptatt-flag / ingen slot / type ikke tilbudt
    if (slot && !slot.is_available) {
      return { status: "unavailable", label: t("collab_list.busy", "Opptatt") };
    }
    return { status: "unavailable", label: t("collab_list.unavailable", "Ikke tilgjengelig") };
  };

  const weekdayKeys = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
  const formatDate = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00Z");
    const wd = t(`collab_list.wd_${weekdayKeys[d.getUTCDay()]}`, weekdayKeys[d.getUTCDay()]);
    return `${wd}. ${d.getUTCDate()}.`;
  };

  const todayISO = new Date().toISOString().slice(0, 10);
  const isPast = (dateStr) => dateStr < todayISO;

  return (
    <section data-testid="collab-calendar-list">
      {/* Måned-navigator */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={prevMonth}
          className="p-2 text-white hover:text-[var(--magenta)]"
          data-testid="cal-list-prev"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <h2 className="font-fraunces italic text-2xl text-white capitalize">
          {monthLabel}
        </h2>
        <button
          onClick={nextMonth}
          className="p-2 text-white hover:text-[var(--magenta)]"
          data-testid="cal-list-next"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Konto-dropdown */}
      <div className="mb-4">
        <label className="font-meta text-[10px] tracking-[0.2em] text-white mb-1 block">
          {t("collab_list.account_dropdown", "Vis for konto")}
        </label>
        {accounts.length === 0 ? (
          <p className="text-xs text-white font-meta">
            {t("collab_list.no_accounts", "Ingen kontoer lagt til")}
          </p>
        ) : (
          <select
            value={selectedAccountId || ""}
            onChange={(e) => onSelectAccount(e.target.value || null)}
            className="bg-black border border-white/40 text-white px-3 py-2 font-meta text-sm min-w-[220px]"
            data-testid="cal-list-account-select"
          >
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {(a.platform || "").toUpperCase()} @{a.handle}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[180px_1fr] gap-4">
        {/* Fargekode-legende */}
        <aside className="panel-tight p-3 h-fit">
          <div className="font-meta text-[10px] tracking-[0.2em] text-white mb-2">
            {t("collab.legend_types")}
          </div>
          <div className="flex flex-col gap-1.5">
            {TYPE_ORDER.map((tp) => (
              <div
                key={tp}
                className="px-2 py-1.5 text-white font-meta text-xs tracking-wider text-center"
                style={{ backgroundColor: TYPE_COLOR[tp] }}
              >
                {t(TYPE_LABEL_KEY[tp], TYPE_FALLBACK[tp])}
              </div>
            ))}
          </div>
        </aside>

        {/* Scrollbar datoliste */}
        <div
          className="space-y-2 max-h-[70vh] overflow-y-auto pr-1"
          data-testid="cal-list-scroll"
        >
          {days.map((date) => {
            const past = isPast(date);
            return (
              <div
                key={date}
                className="panel-tight p-3"
                data-testid={`cal-list-row-${date}`}
              >
                <div className="font-meta text-xs text-white mb-1.5 capitalize">
                  {formatDate(date)}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                  {TYPE_ORDER.map((tp) => {
                    const cell = cellFor(date, tp);
                    const color = TYPE_COLOR[tp];
                    return (
                      <button
                        key={tp}
                        onClick={() => !past && onDateClick(date, tp)}
                        disabled={past}
                        data-testid={`cal-list-cell-${date}-${tp}`}
                        className={`px-2 py-2 text-white font-meta text-[10px] leading-tight text-left ${
                          past ? "cursor-default" : "hover:brightness-110 cursor-pointer"
                        }`}
                        style={{ backgroundColor: color }}
                        title={`${t(TYPE_LABEL_KEY[tp], TYPE_FALLBACK[tp])} · ${cell.label}`}
                      >
                        <div className="font-bold text-[9px] tracking-widest mb-0.5 text-white">
                          {t(TYPE_LABEL_KEY[tp], TYPE_FALLBACK[tp])}
                        </div>
                        <div className="truncate text-white">{cell.label}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
